import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  CargoClaimSummary,
  CreateCargoClaimCommandInput,
  GetCargoClaimCommandOutput,
  ListCargoClaimsCommandInput,
  ListCargoClaimsCommandOutput,
  UpdateCargoClaimAttachmentsCommandInput,
  UpdateCargoClaimCommandInput,
} from "@amzn/ttechclaimintakeservice-client";
import { compareKeys } from "src/forms/tools";
import { CARGO_STAGES } from "./types";
import { createCargoClaimService } from "src/api/cargo/createCargoClaim";
import { ClaimSubmittedStatus } from "../types";
import {
  ResponseField,
  SERVER_ERROR_RESPONSES,
  ProcessedApiResponse,
} from "../../api/types";
import { updateCargoClaimService } from "../../api/cargo/updateCargoClaim";
import { updateCargoClaimAttachmentsService } from "../../api/cargo/updateCargoClaimAttachments";
import { listCargoClaimService } from "../../api/cargo/listCargoClaims";
import { getCargoClaimService } from "../../api/cargo/getCargoClaim";
import { setValueInAppState } from "../appSlice";
// Define a type for the slice state
interface CargoAppState {
  [index: string]: any;

  currentStage: CARGO_STAGES;
  duplicateClaimChecked: boolean;
  duplicateClaim: string;
  submitScreenStatus: ClaimSubmittedStatus;
  createCargoClaimResponse?: ProcessedApiResponse;
  claimNumber?: string;
  cargoClaimsList: CargoClaimSummary[];
  fetchingClaimsList: boolean;
  listCargoClaimsError: string;
  loadingCargoClaim: boolean;
  cargoSubmittedStage?: string;
  cargoIncidentDate?: string;
  claimSubmitSuccess: boolean;
  isTimeoutWorkaround: boolean;
  timeoutWorkaroundStartTime?: number;
}
export const initialState: CargoAppState = {
  currentStage: CARGO_STAGES.STAGE1,
  duplicateClaimChecked: false,
  duplicateClaim: "",
  submitScreenStatus: ClaimSubmittedStatus.LOADING,
  cargoClaimsList: [],
  claimNumber: "",
  fetchingClaimsList: false,
  listCargoClaimsError: "",
  loadingCargoClaim: false,
  cargoSubmittedStage: "",
  cargoIncidentDate: "",
  claimSubmitSuccess: false,
  isTimeoutWorkaround: false,
  timeoutWorkaroundStartTime: undefined,
};

export const verifyStateIsValid = (state: CargoAppState) => {
  return compareKeys(state, initialState);
};

export const createCargoClaim = createAsyncThunk(
  "app/createCargoClaim",
  async (
    params: { createCargoClaimInput: CreateCargoClaimCommandInput },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    thunkAPI: any
  ) => {
    const { createCargoClaimInput } = params;
    try {
      const response = await createCargoClaimService(createCargoClaimInput);
      return response;
    } catch (e) {
      // Include amazonClaimReferenceId in the error payload for error processing
      return thunkAPI.rejectWithValue({
        ...(typeof e === "object" && e !== null ? e : {}),
        amazonClaimReferenceId: createCargoClaimInput.amazonClaimReferenceId,
      });
    }
  }
);

export const updateCargoClaim = createAsyncThunk(
  "app/updateCargoClaim",
  async (
    params: { input: UpdateCargoClaimCommandInput },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    thunkAPI: any
  ) => {
    const { input } = params;
    try {
      const response = await updateCargoClaimService(input);
      return response;
    } catch (e) {
      // Include amazonClaimReferenceId in the error payload for error processing
      return thunkAPI.rejectWithValue({
        ...(typeof e === "object" && e !== null ? e : {}),
        amazonClaimReferenceId: input.amazonClaimReferenceId,
      });
    }
  }
);

export const updateCargoClaimAttachments = createAsyncThunk(
  "app/updateCargoClaimAttachments",
  async (
    params: { input: UpdateCargoClaimAttachmentsCommandInput },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    thunkAPI: any
  ) => {
    const { input } = params;
    try {
      const response = await updateCargoClaimAttachmentsService(input);
      return response;
    } catch (e) {
      // Include amazonClaimReferenceId in the error payload for error processing
      return thunkAPI.rejectWithValue({
        ...(typeof e === "object" && e !== null ? e : {}),
        amazonClaimReferenceId: input.amazonClaimReferenceId,
      });
    }
  }
);

export const listCargoClaims = createAsyncThunk(
  "app/listCargoClaims",
  async (
    params: { input: ListCargoClaimsCommandInput },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    thunkAPI: any
  ) => {
    const { input } = params;
    try {
      const response = await listCargoClaimService(input);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const getCargoClaim = createAsyncThunk(
  "cargoApp/getCargoClaim",
  async (
    params: {
      claimNumber: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    const { claimNumber } = params;
    try {
      const getClaimResponse = await getCargoClaimService({
        claimNumber,
      });

      // Update amazonClaimReferenceId in appSlice if it exists in the response
      if (getClaimResponse.amazonClaimReferenceId) {
        dispatch(
          setValueInAppState({
            key: "amazonClaimReferenceId",
            value: getClaimResponse.amazonClaimReferenceId,
          })
        );
      }

      return getClaimResponse;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

const processSubmitErrors = (
  state: CargoAppState,
  action: PayloadAction<any>
) => {
  const data: ResponseField[] = [];
  const { message, errors, $metadata, warningList, amazonClaimReferenceId } =
    action.payload;
  const statusCode = $metadata?.httpStatusCode?.toString();
  if (SERVER_ERROR_RESPONSES.includes(statusCode)) {
    data.push({
      fieldName: "Amazon Claim Reference ID",
      fieldValue: amazonClaimReferenceId,
    });
  }

  if (errors?.length) {
    data.push(...errors);
  }
  const processedResponse: ProcessedApiResponse = {
    data,
    statusCode,
    warningList,
    message,
  };
  state.createCargoClaimResponse = processedResponse;
};
export const cargoAppSlice = createSlice({
  name: "cargoApp",
  initialState,
  reducers: {
    setValueInCargoAppState: (
      state: CargoAppState,
      action: PayloadAction<{
        key: string;
        value: any;
      }>
    ) => {
      const { key, value } = action.payload;
      state[key as keyof CargoAppState] = value;
    },
    setCargoStage: (
      state: CargoAppState,
      action: PayloadAction<{
        stage: CARGO_STAGES;
      }>
    ) => {
      const { stage } = action.payload;
      state.currentStage = stage;
    },
    setClaimNumber: (
      state: CargoAppState,
      action: PayloadAction<{
        value: string;
      }>
    ) => {
      const { value } = action.payload;
      state.claimNumber = value;
    },
    resetCargoAppState: () => {
      return {
        ...initialState,
      };
    },
    startTimeoutWorkaround: (
      state: CargoAppState,
      action: PayloadAction<{
        claimNumber: string;
      }>
    ) => {
      const { claimNumber } = action.payload;
      state.isTimeoutWorkaround = true;
      state.timeoutWorkaroundStartTime = Date.now();
      state.claimNumber = claimNumber;
      state.submitScreenStatus = ClaimSubmittedStatus.LOADING;
    },
    resetTimeoutWorkaround: (state: CargoAppState) => {
      state.isTimeoutWorkaround = false;
      state.timeoutWorkaroundStartTime = undefined;
    },
  },
  extraReducers: (builder: any) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder

      .addCase(
        createCargoClaim.fulfilled,
        (state: CargoAppState, action: PayloadAction<any>) => {
          state.createCargoClaimResponse = action.payload;
          state.submitScreenStatus = ClaimSubmittedStatus.SUCCESS;
        }
      )
      .addCase(createCargoClaim.pending, (state: CargoAppState) => {
        state.submitScreenStatus = ClaimSubmittedStatus.LOADING;
      })
      .addCase(
        createCargoClaim.rejected,
        (state: CargoAppState, action: PayloadAction<any>) => {
          state.submitScreenStatus = ClaimSubmittedStatus.ERROR;
          processSubmitErrors(state, action);
        }
      )
      .addCase(
        updateCargoClaim.fulfilled,
        (state: CargoAppState, action: PayloadAction<any>) => {
          state.createCargoClaimResponse = action.payload;
          state.submitScreenStatus = ClaimSubmittedStatus.SUCCESS;
        }
      )
      .addCase(updateCargoClaim.pending, (state: CargoAppState) => {
        state.submitScreenStatus = ClaimSubmittedStatus.LOADING;
      })
      .addCase(
        updateCargoClaim.rejected,
        (state: CargoAppState, action: PayloadAction<any>) => {
          state.submitScreenStatus = ClaimSubmittedStatus.ERROR;
          processSubmitErrors(state, action);
        }
      )
      .addCase(
        updateCargoClaimAttachments.fulfilled,
        (state: CargoAppState, action: PayloadAction<any>) => {
          state.createCargoClaimResponse = action.payload;
          state.submitScreenStatus = ClaimSubmittedStatus.SUCCESS;
        }
      )
      .addCase(updateCargoClaimAttachments.pending, (state: CargoAppState) => {
        state.submitScreenStatus = ClaimSubmittedStatus.LOADING;
      })
      .addCase(
        updateCargoClaimAttachments.rejected,
        (state: CargoAppState, action: PayloadAction<any>) => {
          // Check if this is a timeout error (504 Gateway Timeout)
          const statusCode =
            action.payload?.$metadata?.httpStatusCode?.toString();
          const isTimeoutError = statusCode === "504";

          if (isTimeoutError && state.currentStage === CARGO_STAGES.STAGE3) {
            // Start timeout workaround for Stage 3 submissions
            state.isTimeoutWorkaround = true;
            state.timeoutWorkaroundStartTime = Date.now();
            state.submitScreenStatus = ClaimSubmittedStatus.LOADING;
            // Clear cargoSubmittedStage to prevent premature result processing
            state.cargoSubmittedStage = "";
            // claimNumber should already be set in state from previous stages
            // Don't set error state yet - we'll wait and check the claim status
          } else {
            // Handle non-timeout errors normally
            state.submitScreenStatus = ClaimSubmittedStatus.ERROR;
            processSubmitErrors(state, action);
          }
        }
      )
      .addCase(
        listCargoClaims.fulfilled,
        (
          state: CargoAppState,
          action: PayloadAction<ListCargoClaimsCommandOutput>
        ) => {
          state.cargoClaimsList = action.payload.claimsList ?? [];
          state.fetchingClaimsList = false;
          state.listCargoClaimsError = "";
        }
      )
      .addCase(listCargoClaims.pending, (state: CargoAppState) => {
        state.fetchingClaimsList = true;
        state.listCargoClaimsError = "";
        state.cargoClaimsList = [];
      })
      .addCase(listCargoClaims.rejected, (state: CargoAppState) => {
        state.fetchingClaimsList = false;
        state.listCargoClaimsError = "Error fetching cargo claims list";
        state.cargoClaimsList = [];
      })
      .addCase(
        getCargoClaim.fulfilled,
        (
          state: CargoAppState,
          action: PayloadAction<GetCargoClaimCommandOutput>
        ) => {
          // amazonClaimReferenceId is now updated via dispatch in the thunk
          state.cargoSubmittedStage = action.payload.submittedStage ?? "";
          state.loadingCargoClaim = false;
          state.cargoIncidentDate = action.payload.incidentDate;
        }
      )
      .addCase(getCargoClaim.pending, (state: CargoAppState) => {
        state.loadingCargoClaim = true;
      })
      .addCase(getCargoClaim.rejected, (state: CargoAppState) => {
        state.loadingCargoClaim = false;
      });
  },
});

export const {
  setValueInCargoAppState,
  resetCargoAppState,
  setCargoStage,
  setClaimNumber,
  startTimeoutWorkaround,
  resetTimeoutWorkaround,
} = cargoAppSlice.actions;

export default cargoAppSlice.reducer;
